// TODO: https://www.w3.org/TR/2017/NOTE-wai-aria-practices-1.1-20171214/examples/treeview/treeview-2/treeview-2a.html
// Fully accessibility support
import * as React from 'react';
import classNames from 'classnames';
import isPromise from 'is-promise';
import KeyCode from '../../_util/KeyCode';
import warning from '../../_util/warning';
import { TreeContext } from './contextTypes';
import { arrAdd, arrDel, calcDropPosition, calcSelectedKeys, conductExpandParent, getDataAndAria, getDragChildrenKeys, parseCheckedKeys, posToArr, } from './util';
import { convertDataToEntities, convertNodePropsToEventData, convertTreeToData, flattenTreeData, getTreeNodeProps, warningWithoutKey, } from './utils/treeUtil';
import NodeList, { MOTION_KEY, MotionEntity } from './NodeList';
import TreeNode from './TreeNode';
import { conductCheck } from './utils/conductUtil';
import DropIndicator from './DropIndicator';
class Tree extends React.Component {
    constructor() {
        super(...arguments);
        this.destroyed = false;
        this.state = {
            keyEntities: {},
            indent: null,
            selectedKeys: [],
            checkedKeys: [],
            halfCheckedKeys: [],
            loadedKeys: [],
            loadingKeys: [],
            expandedKeys: [],
            dragging: false,
            dragChildrenKeys: [],
            // dropTargetKey is the key of abstract-drop-node
            // the abstract-drop-node is the real drop node when drag and drop
            // not the DOM drag over node
            dropTargetKey: null,
            dropPosition: null,
            dropContainerKey: null,
            dropLevelOffset: null,
            dropTargetPos: null,
            dropAllowed: true,
            // the abstract-drag-over-node
            // if mouse is on the bottom of top dom node or no the top of the bottom dom node
            // abstract-drag-over-node is the top node
            dragOverNodeKey: null,
            treeData: [],
            flattenNodes: [],
            focused: false,
            activeKey: null,
            listChanging: false,
            prevProps: null,
        };
        this.dragStartMousePosition = null;
        this.listRef = React.createRef();
        this.onNodeDragStart = (event, node) => {
            const { expandedKeys, keyEntities } = this.state;
            const { onDragStart } = this.props;
            const { eventKey } = node.props;
            this.dragNode = node;
            this.dragStartMousePosition = {
                x: event.clientX,
                y: event.clientY,
            };
            const newExpandedKeys = arrDel(expandedKeys, eventKey);
            const { current } = this.listRef;
            this.setState({
                dragging: true,
                dragChildrenKeys: getDragChildrenKeys(eventKey, keyEntities),
                indent: current && current.getIndentWidth(),
            });
            this.setExpandedKeys(newExpandedKeys);
            window.addEventListener('dragend', this.onWindowDragEnd);
            if (onDragStart && node) {
                onDragStart({ event, node: convertNodePropsToEventData(node.props) });
            }
        };
        /**
         * [Legacy] Select handler is smaller than node,
         * so that this will trigger when drag enter node or select handler.
         * This is a little tricky if customize css without padding.
         * Better for use mouse move event to refresh drag state.
         * But let's just keep it to avoid event trigger logic change.
         */
        this.onNodeDragEnter = (event, node) => {
            const { expandedKeys, keyEntities, dragChildrenKeys, flattenNodes, indent, } = this.state;
            const { onDragEnter, onExpand, allowDrop, direction } = this.props;
            const { pos } = node.props;
            const { dragNode } = this;
            const { dropPosition, dropLevelOffset, dropTargetKey, dropContainerKey, dropTargetPos, dropAllowed, dragOverNodeKey, } = calcDropPosition(event, node, indent, this.dragStartMousePosition, allowDrop, flattenNodes, keyEntities, direction);
            if (!dragNode ||
                // don't allow drop inside its children
                dragChildrenKeys.indexOf(dropTargetKey) !== -1 ||
                // don't allow drop when drop is not allowed caculated by calcDropPosition
                !dropAllowed) {
                this.setState({
                    dragOverNodeKey: null,
                    dropPosition: null,
                    dropLevelOffset: null,
                    dropTargetKey: null,
                    dropContainerKey: null,
                    dropTargetPos: null,
                    dropAllowed: false,
                });
                return;
            }
            // Side effect for delay drag
            if (!this.delayedDragEnterLogic) {
                this.delayedDragEnterLogic = {};
            }
            Object.keys(this.delayedDragEnterLogic).forEach(key => {
                clearTimeout(this.delayedDragEnterLogic[key]);
            });
            if (dragNode.props.eventKey !== node.props.eventKey) {
                // hoist expand logic here
                // since if logic is on the bottom
                // it will be blocked by abstract dragover node check
                //   => if you dragenter from top, you mouse will still be consider as in the top node
                event.persist();
                this.delayedDragEnterLogic[pos] = window.setTimeout(() => {
                    const { dragging } = this.state;
                    if (!dragging)
                        return;
                    let newExpandedKeys = [...expandedKeys];
                    const entity = keyEntities[node.props.eventKey];
                    if (entity && (entity.children || []).length) {
                        newExpandedKeys = arrAdd(expandedKeys, node.props.eventKey);
                    }
                    if (!('expandedKeys' in this.props)) {
                        this.setExpandedKeys(newExpandedKeys);
                    }
                    if (onExpand) {
                        onExpand(newExpandedKeys, {
                            node: convertNodePropsToEventData(node.props),
                            expanded: true,
                            nativeEvent: event.nativeEvent,
                        });
                    }
                }, 800);
            }
            // Skip if drag node is self
            if (dragNode.props.eventKey === dropTargetKey && dropLevelOffset === 0) {
                this.setState({
                    dragOverNodeKey: null,
                    dropPosition: null,
                    dropLevelOffset: null,
                    dropTargetKey: null,
                    dropContainerKey: null,
                    dropTargetPos: null,
                    dropAllowed: false,
                });
                return;
            }
            // Update drag over node and drag state
            this.setState({
                dragOverNodeKey,
                dropPosition,
                dropLevelOffset,
                dropTargetKey,
                dropContainerKey,
                dropTargetPos,
                dropAllowed,
            });
            if (onDragEnter) {
                onDragEnter({
                    event,
                    node: convertNodePropsToEventData(node.props),
                    expandedKeys,
                });
            }
        };
        this.onNodeDragOver = (event, node) => {
            const { dragChildrenKeys, flattenNodes, keyEntities, indent, } = this.state;
            const { onDragOver, allowDrop, direction } = this.props;
            const { dragNode } = this;
            const { dropPosition, dropLevelOffset, dropTargetKey, dropContainerKey, dropAllowed, dropTargetPos, dragOverNodeKey, } = calcDropPosition(event, node, indent, this.dragStartMousePosition, allowDrop, flattenNodes, keyEntities, direction);
            if (!dragNode ||
                dragChildrenKeys.indexOf(dropTargetKey) !== -1 ||
                !dropAllowed) {
                // don't allow drop inside its children
                // don't allow drop when drop is not allowed caculated by calcDropPosition
                return;
            }
            // Update drag position
            const { dropPosition: stateDropPosition, dropLevelOffset: stateDropLevelOffset, dropTargetKey: stateDropTargetKey, dropContainerKey: stateDropContainerKey, dropTargetPos: stateDropTargetPos, dropAllowed: stateDropAllowed, dragOverNodeKey: stateDragOverNodeKey, } = this.state;
            if (dragNode.props.eventKey === dropTargetKey && dropLevelOffset === 0) {
                if (!(stateDropPosition === null &&
                    stateDropLevelOffset === null &&
                    stateDropTargetKey === null &&
                    stateDropContainerKey === null &&
                    stateDropTargetPos === null &&
                    stateDropAllowed === false &&
                    stateDragOverNodeKey === null)) {
                    this.setState({
                        dropPosition: null,
                        dropLevelOffset: null,
                        dropTargetKey: null,
                        dropContainerKey: null,
                        dropTargetPos: null,
                        dropAllowed: false,
                        dragOverNodeKey: null,
                    });
                }
            }
            else if (!(dropPosition === stateDropPosition &&
                dropLevelOffset === stateDropLevelOffset &&
                dropTargetKey === stateDropTargetKey &&
                dropContainerKey === stateDropContainerKey &&
                dropTargetPos === stateDropTargetPos &&
                dropAllowed === stateDropAllowed &&
                dragOverNodeKey === stateDragOverNodeKey)) {
                this.setState({
                    dropPosition,
                    dropLevelOffset,
                    dropTargetKey,
                    dropContainerKey,
                    dropTargetPos,
                    dropAllowed,
                    dragOverNodeKey,
                });
            }
            if (onDragOver) {
                onDragOver({ event, node: convertNodePropsToEventData(node.props) });
            }
        };
        this.onNodeDragLeave = (event, node) => {
            const { onDragLeave } = this.props;
            if (onDragLeave && node) {
                onDragLeave({ event, node: convertNodePropsToEventData(node.props) });
            }
        };
        // since stopPropagation() is called in treeNode
        // if onWindowDrag is called, whice means state is keeped, drag state should be cleared
        this.onWindowDragEnd = event => {
            this.onNodeDragEnd(event, null, true);
            window.removeEventListener('dragend', this.onWindowDragEnd);
        };
        // if onNodeDragEnd is called, onWindowDragEnd won't be called since stopPropagation() is called
        this.onNodeDragEnd = (event, node, outsideTree = false) => {
            const { onDragEnd } = this.props;
            this.setState({
                dragOverNodeKey: null,
            });
            this.cleanDragState();
            if (onDragEnd && !outsideTree && node) {
                onDragEnd({ event, node: convertNodePropsToEventData(node.props) });
            }
            this.dragNode = null;
        };
        this.onNodeDrop = async (event, _node, outsideTree = false) => {
            const { dragChildrenKeys, dropPosition, dropTargetKey, dropTargetPos, dropAllowed, keyEntities, } = this.state;
            if (!dropAllowed)
                return;
            const { onDrop, onDropBefore } = this.props;
            this.setState({
                dragOverNodeKey: null,
            });
            this.cleanDragState();
            if (dropTargetKey === null)
                return;
            const activeItem = this.getActiveItem();
            const abstractDropNodeProps = {
                ...getTreeNodeProps(dropTargetKey, this.getTreeNodeRequiredProps()),
                active: activeItem ? activeItem.data.key === dropTargetKey : false,
                data: keyEntities[dropTargetKey].node,
            };
            const dropToChild = dragChildrenKeys.indexOf(dropTargetKey) !== -1;
            warning(!dropToChild, 'Can not drop to dragNode\'s children node. This is a bug of rc-tree. Please report an issue.');
            const posArr = dropTargetPos ? posToArr(dropTargetPos) : [];
            const dropResult = {
                event,
                node: convertNodePropsToEventData(abstractDropNodeProps),
                dragNode: this.dragNode ? convertNodePropsToEventData(this.dragNode.props) : null,
                dragNodesKeys: this.dragNode ? [this.dragNode.props.eventKey].concat(dragChildrenKeys) : dragChildrenKeys,
                dropToGap: dropPosition !== 0,
                dropPosition: dropPosition === null ? 0 : dropPosition + Number(posArr[posArr.length - 1]),
            };
            if (onDropBefore && !outsideTree) {
                const resultStatus = onDropBefore(dropResult);
                const result = isPromise(resultStatus) ? await resultStatus : resultStatus;
                if (!result) {
                    return;
                }
            }
            if (onDrop && !outsideTree) {
                onDrop(dropResult);
            }
            this.dragNode = null;
        };
        this.cleanDragState = () => {
            const { dragging } = this.state;
            if (dragging) {
                this.setState({
                    dragging: false,
                    dropPosition: null,
                    dropContainerKey: null,
                    dropTargetKey: null,
                    dropLevelOffset: null,
                    dropAllowed: true,
                    dragOverNodeKey: null,
                });
            }
            this.dragStartMousePosition = null;
        };
        this.onNodeClick = (e, treeNode) => {
            const { onClick } = this.props;
            if (onClick) {
                onClick(e, treeNode);
            }
        };
        this.onNodeDoubleClick = (e, treeNode) => {
            const { onDoubleClick } = this.props;
            if (onDoubleClick) {
                onDoubleClick(e, treeNode);
            }
        };
        this.onNodeSelect = (e, treeNode) => {
            let { selectedKeys } = this.state;
            const { keyEntities } = this.state;
            const { onSelect, multiple } = this.props;
            const { selected, key } = treeNode;
            const targetSelected = !selected;
            // Update selected keys
            if (!targetSelected) {
                selectedKeys = arrDel(selectedKeys, key);
            }
            else if (!multiple) {
                selectedKeys = [key];
            }
            else {
                selectedKeys = arrAdd(selectedKeys, key);
            }
            // [Legacy] Not found related usage in doc or upper libs
            const selectedNodes = selectedKeys
                .map(selectedKey => {
                const entity = keyEntities[selectedKey];
                if (!entity)
                    return null;
                return entity.node;
            })
                .filter(node => node);
            this.setUncontrolledState({ selectedKeys });
            if (onSelect) {
                onSelect(selectedKeys, {
                    event: 'select',
                    selected: targetSelected,
                    node: treeNode,
                    selectedNodes,
                    nativeEvent: e.nativeEvent,
                });
            }
        };
        this.onNodeCheck = (e, treeNode, checked) => {
            const { keyEntities, checkedKeys: oriCheckedKeys, halfCheckedKeys: oriHalfCheckedKeys, } = this.state;
            const { checkStrictly, onCheck } = this.props;
            const { key } = treeNode;
            // Prepare trigger arguments
            let checkedObj;
            let oldCheckedObj;
            const eventObj = {
                event: 'check',
                node: treeNode,
                checked,
                nativeEvent: e.nativeEvent,
            };
            if (checkStrictly) {
                const checkedKeys = checked ? arrAdd(oriCheckedKeys, key) : arrDel(oriCheckedKeys, key);
                const halfCheckedKeys = arrDel(oriHalfCheckedKeys, key);
                checkedObj = { checked: checkedKeys, halfChecked: halfCheckedKeys };
                oldCheckedObj = { checked: oriCheckedKeys, halfChecked: oriHalfCheckedKeys };
                eventObj.checkedNodes = checkedKeys
                    .map(checkedKey => keyEntities[checkedKey])
                    .filter(entity => entity)
                    .map(entity => entity.node);
                this.setUncontrolledState({ checkedKeys });
            }
            else {
                // Always fill first
                let { checkedKeys, halfCheckedKeys } = conductCheck([...oriCheckedKeys, key], true, keyEntities);
                // If remove, we do it again to correction
                if (!checked) {
                    const keySet = new Set(checkedKeys);
                    keySet.delete(key);
                    ({ checkedKeys, halfCheckedKeys } = conductCheck(Array.from(keySet), { checked: false, halfCheckedKeys }, keyEntities));
                }
                checkedObj = checkedKeys;
                oldCheckedObj = oriCheckedKeys;
                // [Legacy] This is used for `rc-tree-select`
                const checkedNodes = [];
                const checkedNodesPositions = [];
                eventObj.halfCheckedKeys = halfCheckedKeys;
                checkedKeys.forEach(checkedKey => {
                    const entity = keyEntities[checkedKey];
                    if (!entity)
                        return;
                    const { node, pos } = entity;
                    checkedNodes.push(node);
                    checkedNodesPositions.push({ node, pos });
                });
                eventObj.checkedNodes = checkedNodes;
                eventObj.checkedNodesPositions = checkedNodesPositions;
                this.setUncontrolledState({
                    checkedKeys,
                }, false, {
                    halfCheckedKeys,
                });
            }
            if (onCheck) {
                onCheck(checkedObj, eventObj, oldCheckedObj);
            }
        };
        this.onNodeLoad = (treeNode) => new Promise(resolve => {
            // We need to get the latest state of loading/loaded keys
            this.setState(({ loadedKeys = [], loadingKeys = [] }) => {
                const { loadData, onLoad } = this.props;
                const { key } = treeNode;
                if (!loadData || loadedKeys.indexOf(key) !== -1 || loadingKeys.indexOf(key) !== -1) {
                    // react 15 will warn if return null
                    return {};
                }
                // Process load data
                const promise = loadData(treeNode);
                promise.then(() => {
                    const { loadedKeys: currentLoadedKeys, loadingKeys: currentLoadingKeys } = this.state;
                    const newLoadedKeys = arrAdd(currentLoadedKeys, key);
                    const newLoadingKeys = arrDel(currentLoadingKeys, key);
                    // onLoad should trigger before internal setState to avoid `loadData` trigger twice.
                    // https://github.com/ant-design/ant-design/issues/12464
                    if (onLoad) {
                        onLoad(newLoadedKeys, {
                            event: 'load',
                            node: treeNode,
                        });
                    }
                    this.setUncontrolledState({
                        loadedKeys: newLoadedKeys,
                    });
                    this.setState({
                        loadingKeys: newLoadingKeys,
                    });
                    resolve();
                });
                return {
                    loadingKeys: arrAdd(loadingKeys, key),
                };
            });
        });
        this.onNodeMouseEnter = (event, node) => {
            const { onMouseEnter } = this.props;
            if (onMouseEnter) {
                onMouseEnter({ event, node });
            }
        };
        this.onNodeMouseLeave = (event, node) => {
            const { onMouseLeave } = this.props;
            if (onMouseLeave) {
                onMouseLeave({ event, node });
            }
        };
        this.onNodeContextMenu = (event, node) => {
            const { onRightClick } = this.props;
            if (onRightClick) {
                event.preventDefault();
                onRightClick({ event, node });
            }
        };
        this.onFocus = (...args) => {
            const { onFocus } = this.props;
            this.setState({ focused: true });
            if (onFocus) {
                onFocus(...args);
            }
        };
        this.onBlur = (...args) => {
            const { onBlur } = this.props;
            this.setState({ focused: false });
            this.onActiveChange(null);
            if (onBlur) {
                onBlur(...args);
            }
        };
        this.getTreeNodeRequiredProps = () => {
            const { expandedKeys, selectedKeys, loadedKeys, loadingKeys, checkedKeys, halfCheckedKeys, dragOverNodeKey, dropPosition, keyEntities, } = this.state;
            return {
                expandedKeys: expandedKeys || [],
                selectedKeys: selectedKeys || [],
                loadedKeys: loadedKeys || [],
                loadingKeys: loadingKeys || [],
                checkedKeys: checkedKeys || [],
                halfCheckedKeys: halfCheckedKeys || [],
                dragOverNodeKey,
                dropPosition,
                keyEntities,
            };
        };
        // =========================== Expanded ===========================
        /** Set uncontrolled `expandedKeys`. This will also auto update `flattenNodes`. */
        this.setExpandedKeys = (expandedKeys) => {
            const { treeData } = this.state;
            const flattenNodes = flattenTreeData(treeData, expandedKeys);
            this.setUncontrolledState({
                expandedKeys,
                flattenNodes,
            }, true);
        };
        this.onNodeExpand = (e, treeNode) => {
            let { expandedKeys } = this.state;
            const { listChanging, treeData } = this.state;
            const { onExpand, loadData } = this.props;
            const { key, expanded } = treeNode;
            // Do nothing when motion is in progress
            if (listChanging) {
                return;
            }
            // Update selected keys
            const index = expandedKeys.indexOf(key);
            const targetExpanded = !expanded;
            warning((expanded && index !== -1) || (!expanded && index === -1), 'Expand state not sync with index check');
            if (targetExpanded) {
                expandedKeys = arrAdd(expandedKeys, key);
            }
            else {
                expandedKeys = arrDel(expandedKeys, key);
            }
            this.setExpandedKeys(expandedKeys);
            if (onExpand) {
                onExpand(expandedKeys, {
                    node: treeNode,
                    expanded: targetExpanded,
                    nativeEvent: e.nativeEvent,
                });
            }
            // Async Load data
            if (targetExpanded && loadData) {
                const loadPromise = this.onNodeLoad(treeNode);
                if (loadPromise) {
                    loadPromise.then(() => {
                        // [Legacy] Refresh logic
                        const newFlattenTreeData = flattenTreeData(treeData, expandedKeys);
                        this.setUncontrolledState({ flattenNodes: newFlattenTreeData });
                    });
                }
            }
        };
        this.onListChangeStart = () => {
            this.setUncontrolledState({
                listChanging: true,
            });
        };
        this.onListChangeEnd = () => {
            setTimeout(() => {
                this.setUncontrolledState({
                    listChanging: false,
                });
            });
        };
        // =========================== Keyboard ===========================
        this.onActiveChange = (newActiveKey) => {
            const { activeKey } = this.state;
            const { onActiveChange } = this.props;
            if (activeKey === newActiveKey) {
                return;
            }
            this.setState({ activeKey: newActiveKey });
            if (newActiveKey !== null) {
                this.scrollTo({ key: newActiveKey });
            }
            if (onActiveChange) {
                onActiveChange(newActiveKey);
            }
        };
        this.getActiveItem = () => {
            const { activeKey, flattenNodes } = this.state;
            if (activeKey === null) {
                return null;
            }
            return flattenNodes.find(({ data: { key } }) => key === activeKey) || null;
        };
        this.offsetActiveKey = (offset) => {
            const { flattenNodes, activeKey } = this.state;
            let index = flattenNodes.findIndex(({ data: { key } }) => key === activeKey);
            // Align with index
            if (index === -1 && offset < 0) {
                index = flattenNodes.length;
            }
            index = (index + offset + flattenNodes.length) % flattenNodes.length;
            const item = flattenNodes[index];
            if (item) {
                const { key } = item.data;
                this.onActiveChange(key);
            }
            else {
                this.onActiveChange(null);
            }
        };
        this.onKeyDown = event => {
            const { activeKey, expandedKeys, checkedKeys } = this.state;
            const { onKeyDown, checkable, selectable, loadData } = this.props;
            // >>>>>>>>>> Direction
            switch (event.which) {
                case KeyCode.UP: {
                    this.offsetActiveKey(-1);
                    event.preventDefault();
                    break;
                }
                case KeyCode.DOWN: {
                    this.offsetActiveKey(1);
                    event.preventDefault();
                    break;
                }
                default:
            }
            // >>>>>>>>>> Expand & Selection
            const activeItem = this.getActiveItem();
            if (activeItem && activeItem.data) {
                const treeNodeRequiredProps = this.getTreeNodeRequiredProps();
                const expandable = activeItem.data.isLeaf === false || !!(activeItem.data.children || []).length || loadData;
                const eventNode = convertNodePropsToEventData({
                    ...getTreeNodeProps(activeKey, treeNodeRequiredProps),
                    data: activeItem.data,
                    active: true,
                });
                switch (event.which) {
                    // >>> Expand
                    case KeyCode.LEFT: {
                        // Collapse if possible
                        if (expandable && expandedKeys.includes(activeKey)) {
                            this.onNodeExpand({}, eventNode);
                        }
                        else if (activeItem.parent) {
                            this.onActiveChange(activeItem.parent.data.key);
                        }
                        event.preventDefault();
                        break;
                    }
                    case KeyCode.RIGHT: {
                        // Expand if possible
                        if (expandable && !expandedKeys.includes(activeKey)) {
                            this.onNodeExpand({}, eventNode);
                        }
                        else if (activeItem.children && activeItem.children.length) {
                            this.onActiveChange(activeItem.children[0].data.key);
                        }
                        event.preventDefault();
                        break;
                    }
                    // Selection
                    case KeyCode.ENTER:
                    case KeyCode.SPACE: {
                        if (checkable &&
                            !eventNode.disabled &&
                            eventNode.checkable !== false &&
                            !eventNode.disableCheckbox) {
                            this.onNodeCheck({}, eventNode, !checkedKeys.includes(activeKey));
                            event.preventDefault();
                        }
                        else if (!checkable &&
                            selectable &&
                            !eventNode.disabled &&
                            eventNode.selectable !== false) {
                            this.onNodeSelect({}, eventNode);
                            event.preventDefault();
                        }
                        break;
                    }
                    default:
                }
            }
            if (onKeyDown) {
                onKeyDown(event);
            }
        };
        /**
         * Only update the value which is not in props
         */
        this.setUncontrolledState = (state, atomic = false, forceState = null) => {
            if (this.destroyed) {
                return;
            }
            let needSync = false;
            let allPassed = true;
            const newState = {};
            Object.keys(state).forEach(name => {
                if (name in this.props) {
                    allPassed = false;
                    return;
                }
                needSync = true;
                newState[name] = state[name];
            });
            if (needSync && (!atomic || allPassed)) {
                this.setState({
                    ...newState,
                    ...forceState,
                });
            }
        };
        this.scrollTo = scroll => {
            const { current } = this.listRef;
            if (current) {
                current.scrollTo(scroll);
            }
        };
    }
    componentWillUnmount() {
        window.removeEventListener('dragend', this.onWindowDragEnd);
        this.destroyed = true;
    }
    static getDerivedStateFromProps(props, prevState) {
        const { prevProps } = prevState;
        const newState = {
            ...prevState,
            prevProps: props,
        };
        function needSync(name) {
            return (!prevProps && name in props) || (prevProps && prevProps[name] !== props[name]);
        }
        // ================== Tree Node ==================
        let treeData;
        // Check if `treeData` or `children` changed and save into the state.
        if (needSync('treeData')) {
            ({ treeData } = props);
        }
        else if (needSync('children')) {
            warning(false, '`children` of Tree is deprecated. Please use `treeData` instead.');
            treeData = convertTreeToData(props.children);
        }
        // Save flatten nodes info and convert `treeData` into keyEntities
        if (treeData) {
            newState.treeData = treeData;
            const entitiesMap = convertDataToEntities(treeData);
            newState.keyEntities = {
                [MOTION_KEY]: MotionEntity,
                ...entitiesMap.keyEntities,
            };
            // Warning if treeNode not provide key
            if (process.env.NODE_ENV !== 'production') {
                warningWithoutKey(treeData);
            }
        }
        const keyEntities = newState.keyEntities || prevState.keyEntities;
        // ================ expandedKeys =================
        if (needSync('expandedKeys') || (prevProps && needSync('autoExpandParent'))) {
            newState.expandedKeys =
                props.autoExpandParent || (!prevProps && props.defaultExpandParent)
                    ? conductExpandParent(props.expandedKeys, keyEntities)
                    : props.expandedKeys;
        }
        else if (!prevProps && props.defaultExpandAll) {
            const cloneKeyEntities = { ...keyEntities };
            delete cloneKeyEntities[MOTION_KEY];
            newState.expandedKeys = Object.keys(cloneKeyEntities).map(key => cloneKeyEntities[key].key);
        }
        else if (!prevProps && props.defaultExpandedKeys) {
            newState.expandedKeys =
                props.autoExpandParent || props.defaultExpandParent
                    ? conductExpandParent(props.defaultExpandedKeys, keyEntities)
                    : props.defaultExpandedKeys;
        }
        if (!newState.expandedKeys) {
            delete newState.expandedKeys;
        }
        // ================ flattenNodes =================
        if (treeData || newState.expandedKeys) {
            const flattenNodes = flattenTreeData(treeData || prevState.treeData, newState.expandedKeys || prevState.expandedKeys);
            newState.flattenNodes = flattenNodes;
        }
        // ================ selectedKeys =================
        if (props.selectable) {
            if (needSync('selectedKeys')) {
                newState.selectedKeys = calcSelectedKeys(props.selectedKeys, props);
            }
            else if (!prevProps && props.defaultSelectedKeys) {
                newState.selectedKeys = calcSelectedKeys(props.defaultSelectedKeys, props);
            }
        }
        // ================= checkedKeys =================
        if (props.checkable) {
            let checkedKeyEntity;
            if (needSync('checkedKeys')) {
                checkedKeyEntity = parseCheckedKeys(props.checkedKeys) || {};
            }
            else if (!prevProps && props.defaultCheckedKeys) {
                checkedKeyEntity = parseCheckedKeys(props.defaultCheckedKeys) || {};
            }
            else if (treeData) {
                // If `treeData` changed, we also need check it
                checkedKeyEntity = parseCheckedKeys(props.checkedKeys) || {
                    checkedKeys: prevState.checkedKeys,
                    halfCheckedKeys: prevState.halfCheckedKeys,
                };
            }
            if (checkedKeyEntity) {
                let { checkedKeys = [], halfCheckedKeys = [] } = checkedKeyEntity;
                if (!props.checkStrictly) {
                    const conductKeys = conductCheck(checkedKeys, true, keyEntities);
                    ({ checkedKeys, halfCheckedKeys } = conductKeys);
                }
                newState.checkedKeys = checkedKeys;
                newState.halfCheckedKeys = halfCheckedKeys;
            }
        }
        // ================= loadedKeys ==================
        if (needSync('loadedKeys')) {
            newState.loadedKeys = props.loadedKeys;
        }
        return newState;
    }
    render() {
        const { focused, flattenNodes, keyEntities, dragging, activeKey, dropLevelOffset, dropContainerKey, dropTargetKey, dropPosition, dragOverNodeKey, indent, } = this.state;
        const { prefixCls, className, style, showLine, focusable, tabIndex = 0, selectable, showIcon, icon, switcherIcon, draggable, checkable, checkStrictly, disabled, motion, loadData, filterTreeNode, height, itemHeight, virtual, titleRender, dropIndicatorRender, onContextMenu, direction, ripple, onMouseDown, } = this.props;
        const domProps = getDataAndAria(this.props);
        return (React.createElement(TreeContext.Provider, { value: {
                prefixCls,
                selectable,
                showIcon,
                icon,
                switcherIcon,
                draggable,
                checkable,
                checkStrictly,
                disabled,
                keyEntities,
                dropLevelOffset,
                dropContainerKey,
                dropTargetKey,
                dropPosition,
                dragOverNodeKey,
                indent,
                direction,
                dropIndicatorRender,
                loadData,
                filterTreeNode,
                titleRender,
                ripple,
                onNodeClick: this.onNodeClick,
                onNodeDoubleClick: this.onNodeDoubleClick,
                onNodeExpand: this.onNodeExpand,
                onNodeSelect: this.onNodeSelect,
                onNodeCheck: this.onNodeCheck,
                onNodeLoad: this.onNodeLoad,
                onNodeMouseEnter: this.onNodeMouseEnter,
                onNodeMouseLeave: this.onNodeMouseLeave,
                onNodeContextMenu: this.onNodeContextMenu,
                onNodeDragStart: this.onNodeDragStart,
                onNodeDragEnter: this.onNodeDragEnter,
                onNodeDragOver: this.onNodeDragOver,
                onNodeDragLeave: this.onNodeDragLeave,
                onNodeDragEnd: this.onNodeDragEnd,
                onNodeDrop: this.onNodeDrop,
            } },
            React.createElement("div", { className: classNames(prefixCls, className, {
                    [`${prefixCls}-show-line`]: showLine,
                    [`${prefixCls}-focused`]: focused,
                    [`${prefixCls}-active-focused`]: activeKey !== null,
                }), onMouseDown: onMouseDown },
                React.createElement(NodeList, Object.assign({ ref: this.listRef, prefixCls: prefixCls, style: style, data: flattenNodes, disabled: disabled, selectable: selectable, checkable: !!checkable, motion: motion, dragging: dragging, height: height, itemHeight: itemHeight, virtual: virtual, focusable: focusable, focused: focused, tabIndex: tabIndex, activeItem: this.getActiveItem(), onFocus: this.onFocus, onBlur: this.onBlur, onKeyDown: this.onKeyDown, onActiveChange: this.onActiveChange, onListChangeStart: this.onListChangeStart, onListChangeEnd: this.onListChangeEnd, onContextMenu: onContextMenu }, this.getTreeNodeRequiredProps(), domProps)))));
    }
}
Tree.defaultProps = {
    prefixCls: 'rc-tree',
    showLine: false,
    showIcon: true,
    selectable: true,
    multiple: false,
    checkable: false,
    disabled: false,
    checkStrictly: false,
    draggable: false,
    defaultExpandParent: true,
    autoExpandParent: false,
    defaultExpandAll: false,
    defaultExpandedKeys: [],
    defaultCheckedKeys: [],
    defaultSelectedKeys: [],
    dropIndicatorRender: DropIndicator,
    allowDrop: () => true,
};
Tree.TreeNode = TreeNode;
export default Tree;
